import type { DeliveryCarrier, DeliveryMode } from './deliveryPricing';
import { deliveryModeToCarrier, isDeliveryCarrier, normalizeDeliveryMode } from './deliveryPricing';

const OFFER_PREFIX = 'Bon de livraison :';
const MODE_TAG_RE = /^\[mode:([a-z_]+)\]\s*/;
const CARRIER_TAG_RE = /^\[carrier:([a-z_]+)\]\s*/;
const AMOUNT_CENTS_TAG_RE = /\[amount_cents:(\d{2,6})\]/;

export const MIN_OFFER_AMOUNT_CENTS = 100;
export const MAX_OFFER_AMOUNT_CENTS = 50000;

const DELIVERY_MODES: (DeliveryMode | 'chronopost_home_49' | 'chronopost_home_4p')[] = [
  'colissimo_home',
  'colissimo_pickup',
  'chronopost_relais_13',
  'chronopost_shop2shop',
  'chronopost_relais_europe',
  'chronopost_relais_dom',
  'chronopost_home_13',
  'chronopost_home_10',
  'chronopost_home_18',
  'mondial_relay_pickup',
  'chronopost_home_49',
  'chronopost_home_4p',
];

function isDeliveryMode(value: string): value is DeliveryMode {
  return (DELIVERY_MODES as string[]).includes(value)
    || value === 'chronopost_home_49'
    || value === 'chronopost_home_4p';
}

export function buildDeliveryOfferMessage(
  carrier: DeliveryCarrier,
  body?: string,
  amountCents?: number | null
): string {
  const defaultBody =
    "J'accepte de restituer l'objet via ce transporteur. Le propriétaire choisira ensuite comment le recevoir (domicile ou point relais) et règlera les frais sur Stripe ; une fois payé, je pourrai expédier le colis.";
  const text = (body?.trim() || defaultBody).trim();
  const amountTag =
    typeof amountCents === 'number'
    && Number.isFinite(amountCents)
    && amountCents >= MIN_OFFER_AMOUNT_CENTS
    && amountCents <= MAX_OFFER_AMOUNT_CENTS
      ? ` [amount_cents:${Math.round(amountCents)}]`
      : '';
  return `${OFFER_PREFIX} [carrier:${carrier}]${amountTag} ${text}`;
}

export function parseDeliveryOfferCarrier(content: string): DeliveryCarrier | null {
  if (!content.startsWith(OFFER_PREFIX)) return null;
  const rest = content.slice(OFFER_PREFIX.length).trimStart();
  const carrierMatch = rest.match(CARRIER_TAG_RE);
  if (carrierMatch?.[1] && isDeliveryCarrier(carrierMatch[1])) {
    return carrierMatch[1];
  }
  const modeMatch = rest.match(MODE_TAG_RE);
  if (modeMatch?.[1] && isDeliveryMode(modeMatch[1])) {
    return deliveryModeToCarrier(normalizeDeliveryMode(modeMatch[1]) ?? modeMatch[1] as DeliveryMode);
  }
  return null;
}

export function parseDeliveryOfferAmountCents(content: string): number | null {
  if (!content.startsWith(OFFER_PREFIX)) return null;
  const match = content.match(AMOUNT_CENTS_TAG_RE);
  if (!match?.[1]) return null;
  const cents = parseInt(match[1], 10);
  if (!Number.isFinite(cents) || cents < MIN_OFFER_AMOUNT_CENTS || cents > MAX_OFFER_AMOUNT_CENTS) {
    return null;
  }
  return cents;
}

export function stripDeliveryOfferModeTag(content: string): string {
  if (!content.startsWith(OFFER_PREFIX)) return content;
  const rest = content.slice(OFFER_PREFIX.length).trimStart();
  return rest
    .replace(CARRIER_TAG_RE, '')
    .replace(MODE_TAG_RE, '')
    .replace(AMOUNT_CENTS_TAG_RE, '')
    .replace(/\[ups_method:[a-z_]+\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isDeliveryOfferMessage(content: string): boolean {
  return content.startsWith(OFFER_PREFIX);
}

export function euroInputToCents(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const num = Number(normalized);
  if (!Number.isFinite(num) || num <= 0) return null;
  const cents = Math.round(num * 100);
  if (cents < MIN_OFFER_AMOUNT_CENTS || cents > MAX_OFFER_AMOUNT_CENTS) return null;
  return cents;
}

export function centsToEuroInput(cents: number): string {
  const eur = cents / 100;
  return Number.isInteger(eur) ? String(eur) : eur.toFixed(2);
}
